<template>
  <VBusHelper
    :reactOn="constants.EVENTS.WALLET.WITHDRAWAL_OPEN"
    :handler="showModal"
  />

  <DialogComponent
    ref="modal"
    dialogId="withdrawal-modal"
    type="bottomsheet"
    noInnerPadding
    @closed="resetForm"
  >
    <div class="withdrawal-modal">
      <!-- Header with gradient -->
      <div class="relative bg-primary rounded-t-3xl px-6 pt-8 pb-10">
        <div class="text-center">
          <div
            class="inline-flex items-center justify-center w-20 h-20 bg-white/20 backdrop-blur-sm rounded-2xl mb-4 shadow-lg"
          >
            <svg
              class="w-10 h-10 text-white"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2.5"
                d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
              />
            </svg>
          </div>
          <h2 class="text-2xl font-bold text-white">Retrait d'argent</h2>
          <p class="text-rose-100 text-sm mt-1">
            Transférez vos fonds vers mobile money
          </p>
        </div>
      </div>

      <!-- Content Card -->
      <div class="relative -mt-6 px-4 pb-4">
        <div
          class="bg-white rounded-3xl shadow-xl shadow-slate-200/50 p-6 space-y-6"
        >
          <!-- Available Balance -->
          <div
            class="bg-gradient-to-r from-slate-50 to-slate-100 border-2 border-slate-200 rounded-2xl p-4"
          >
            <div class="flex items-center justify-between">
              <div class="flex items-center gap-3">
                <div
                  class="w-10 h-10 bg-slate-200 rounded-full flex items-center justify-center"
                >
                  <svg
                    class="w-5 h-5 text-slate-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"
                    />
                  </svg>
                </div>
                <div>
                  <p class="text-xs text-slate-500 font-medium">
                    Solde disponible
                  </p>
                  <p class="text-xl font-bold text-slate-800">
                    {{ formatAmount(balance) }} XOF
                  </p>
                </div>
              </div>
            </div>
          </div>

          <!-- Withdrawal Accounts Selection Section -->
          <div class="space-y-3">
            <div class="flex items-center justify-between">
              <label class="block text-sm font-semibold text-slate-700 flex items-center gap-2">
                <svg
                  class="w-4 h-4 text-slate-500"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"
                  />
                </svg>
                Compte de retrait
              </label>
              <button
                type="button"
                @click="openAddAccountModal"
                class="text-xs font-bold text-rose-500 hover:text-rose-600 flex items-center gap-1 transition-colors"
              >
                <span>+ Ajouter un compte</span>
              </button>
            </div>

            <!-- Loading accounts -->
            <div v-if="accountsLoading" class="p-4 text-center text-xs text-slate-400 bg-slate-50 rounded-2xl border border-slate-100 flex items-center justify-center gap-2">
              <svg class="w-4 h-4 animate-spin text-rose-500" fill="none" viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z"></path>
              </svg>
              <span>Chargement des comptes...</span>
            </div>

            <!-- Accounts List -->
            <div v-else-if="accounts.length > 0" class="space-y-2 max-h-48 overflow-y-auto pr-1">
              <div
                v-for="account in accounts"
                :key="account.id || account.accountNumber"
                @click="selectAccount(account)"
                class="p-3.5 rounded-2xl border-2 transition-all cursor-pointer flex items-center justify-between"
                :class="
                  selectedAccount && selectedAccount.id === account.id
                    ? 'border-rose-500 bg-rose-50/50 shadow-sm'
                    : 'border-slate-200 bg-white hover:border-slate-300'
                "
              >
                <div class="flex items-center gap-3">
                  <!-- Operator Badge -->
                  <div
                    class="w-9 h-9 rounded-xl flex items-center justify-center font-bold text-xs shrink-0"
                    :class="getOperatorBadgeClass(account.operator)"
                  >
                    {{ getOperatorShortName(account.operator) }}
                  </div>
                  <div>
                    <p class="text-sm font-bold text-slate-800 leading-snug">
                      {{ account.accountLabel || 'Compte Mobile Money' }}
                    </p>
                    <p class="text-xs font-mono text-slate-500">
                      {{ formatPhoneNumberDisplay(account.accountNumber) }} ({{ account.operator || 'MTN' }})
                    </p>
                  </div>
                </div>

                <!-- Radio Check -->
                <div
                  class="w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all"
                  :class="
                    selectedAccount && selectedAccount.id === account.id
                      ? 'border-rose-500 bg-rose-500 text-white'
                      : 'border-slate-300 bg-white'
                  "
                >
                  <svg
                    v-if="selectedAccount && selectedAccount.id === account.id"
                    class="w-3 h-3 text-white"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="3" d="M5 13l4 4L19 7" />
                  </svg>
                </div>
              </div>
            </div>

            <!-- Empty Accounts State -->
            <div
              v-else
              class="p-5 rounded-2xl bg-amber-50 border border-amber-200 text-center space-y-3"
            >
              <div class="w-10 h-10 rounded-full bg-amber-100 text-amber-600 flex items-center justify-center mx-auto text-lg">
                ⚠️
              </div>
              <div>
                <p class="text-sm font-bold text-amber-900">Aucun compte de retrait</p>
                <p class="text-xs text-amber-700 mt-0.5">
                  Veuillez ajouter un compte Mobile Money avant de demander un retrait.
                </p>
              </div>
              <button
                type="button"
                @click="openAddAccountModal"
                class="px-4 py-2.5 rounded-xl bg-amber-600 hover:bg-amber-700 text-white font-bold text-xs transition-colors shadow-sm inline-flex items-center gap-1.5"
              >
                <span>+ Ajouter mon compte</span>
              </button>
            </div>
          </div>

          <!-- Amount Section -->
          <div class="space-y-3">
            <label
              class="block text-sm font-semibold text-slate-700 flex items-center gap-2"
            >
              <svg
                class="w-4 h-4 text-rose-500"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2.5"
                  d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4 4m0 0l-4-4m4 4V4"
                />
              </svg>
              Montant à retirer
            </label>

            <!-- Amount Input Display -->
            <div class="relative">
              <div
                class="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none"
              >
                <span class="text-slate-400 font-semibold text-lg">XOF</span>
              </div>
              <input
                v-model="form.amount"
                type="number"
                name="amount"
                :min="payoutLimits.min"
                :max="Math.min(balance, payoutLimits.max)"
                class="placeholder:text-gray-300 w-full pl-16 pr-4 py-4 bg-slate-50 border-2 border-slate-200 rounded-2xl text-3xl font-bold text-slate-800 text-center focus:border-rose-500 focus:bg-white focus:ring-4 focus:ring-rose-500/10 transition-all outline-none"
                :class="{
                  'border-red-300 bg-red-50': errors.amount,
                  'border-rose-500 bg-rose-50': form.amount && !errors.amount
                }"
                placeholder="5000"
                @input="validateAmount"
              />
            </div>

            <div class="flex justify-between text-xs text-slate-500 px-2">
              <span>Min: {{ formatAmount(payoutLimits.min) }} XOF</span>
              <span>Max: {{ formatAmount(payoutLimits.max) }} XOF</span>
            </div>

            <div
              v-if="errors.amount"
              class="flex items-center gap-2 text-sm text-red-600 bg-red-50 px-3 py-2 rounded-xl"
            >
              <svg
                class="w-4 h-4 flex-shrink-0"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              {{ errors.amount }}
            </div>
          </div>

          <!-- Quick Percentages -->
          <div class="space-y-3">
            <label class="block text-sm font-semibold text-slate-700"
              >Retrait rapide (%)</label
            >
            <div class="grid grid-cols-4 gap-2">
              <button
                v-for="percent in quickPercentages"
                :key="percent"
                type="button"
                class="py-3 px-2 rounded-xl font-semibold text-sm transition-all duration-300 border-2"
                :class="
                  form.amount === calculateQuickAmount(percent)
                    ? 'bg-rose-500 border-rose-500 text-white shadow-lg shadow-rose-500/25 scale-105'
                    : 'bg-white border-slate-200 text-slate-700 hover:border-rose-300 hover:bg-rose-50 hover:scale-105'
                "
                @click="selectQuickPercentage(percent)"
              >
                {{ percent }}%
              </button>
            </div>
          </div>

          <!-- Processing Indicator -->
          <div
            v-if="isProcessing"
            class="bg-primary-50 border-2 border-primary-200 rounded-2xl p-4"
          >
            <div class="flex items-center gap-3">
              <div
                class="w-10 h-10 bg-primary-100 rounded-full flex items-center justify-center"
              >
                <svg
                  class="w-5 h-5 text-primary-600 animate-spin"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    class="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    stroke-width="4"></circle>
                  <path
                    class="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
              </div>
              <div class="flex-1">
                <p class="text-sm font-semibold text-primary-800">
                  Traitement en cours...
                </p>
                <p class="text-xs text-primary-600 mt-0.5">
                  Veuillez patienter pendant le transfert
                </p>
              </div>
            </div>
          </div>

          <!-- Submit Button -->
          <button
            type="button"
            class="w-full py-4 rounded-2xl font-bold text-lg transition-all duration-300 active:scale-95 flex items-center justify-center gap-3"
            :class="
              isFormValid && !isProcessing
                ? 'bg-gradient-to-r from-rose-500 to-orange-600 text-white shadow-lg shadow-rose-500/30 hover:shadow-xl hover:shadow-rose-500/40 hover:scale-[1.02]'
                : 'bg-slate-200 text-slate-400 cursor-not-allowed'
            "
            :disabled="!isFormValid || isProcessing"
            @click="handleSubmit"
          >
            <template v-if="!isProcessing">
              <svg
                class="w-6 h-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2.5"
                  d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4 4m0 0l-4-4m4 4V4"
                />
              </svg>
              Confirmer le retrait
            </template>
            <template v-else>
              <svg class="animate-spin w-6 h-6" fill="none" viewBox="0 0 24 24">
                <circle
                  class="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  stroke-width="4"
                ></circle>
                <path
                  class="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                ></path>
              </svg>
              Traitement en cours...
            </template>
          </button>

          <!-- Cancel Button -->
          <button
            v-if="!isProcessing"
            type="button"
            class="w-full py-3.5 rounded-xl font-semibold text-slate-600 hover:bg-slate-100 transition-all duration-300 active:scale-95"
            @click="hideModal"
          >
            Annuler
          </button>
        </div>
      </div>
    </div>
  </DialogComponent>

  <!-- Add New Withdrawal Account Modal Component -->
  <AddNewWithdrawalAccount ref="addAccountModal" />

  <!-- Standalone FedaPay Pilot Verification Modal for Withdrawal Payout -->
  <AddFedapayWithdrawalAccount
    ref="fedapayVerification"
    @verified="onFedapayVerifiedAndWithdraw"
    @cancelled="onFedapayCancelled"
    @error="onFedapayError"
  />
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount } from 'vue';
import { bus } from '@core/events/eventSystem';
import constants from '@core/utils/constants';
import AddNewWithdrawalAccount from './components/AddNewWithdrawalAccount.vue';
import AddFedapayWithdrawalAccount from './components/AddFedapayWithdrawalAccount.vue';
import { getProviderToUse, checkFedapayAccount, getPayoutLimit } from '../serializer.js';

const modal = ref(null);
const addAccountModal = ref(null);
const fedapayVerification = ref(null);

const form = ref({
  amount: ''
});
const errors = ref({
  amount: null
});
const isProcessing = ref(false);
const balance = ref(0);
const payoutLimits = ref({
  min: 1000,
  max: 10000
});

const accounts = ref([]);
const accountsLoading = ref(false);
const selectedAccount = ref(null);

const quickPercentages = [25, 50, 75, 100];

async function showModal() {
  bus.emit(constants.EVENTS.WALLET.BALANCE_LOAD);
  bus.emit(constants.EVENTS.WALLET.ACCOUNTS_LOAD);

  try {
    const limits = await getPayoutLimit('customer');
    if (limits) {
      payoutLimits.value = {
        min: limits.min ?? 1000,
        max: limits.max ?? 10000
      };
    }
  } catch (e) {
    console.error('Erreur chargement limites payout :', e);
  }

  modal.value?.show();
}

function hideModal() {
  modal.value?.hide();
}

function openAddAccountModal() {
  addAccountModal.value?.showModal();
}

function selectAccount(account) {
  selectedAccount.value = account;
}

function formatAmount(amount) {
  return new Intl.NumberFormat('fr-FR', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(amount);
}

function formatPhoneNumberDisplay(phone) {
  if (!phone) return '';
  const clean = String(phone).replace(/\s+/g, '');
  if (clean.startsWith('229') && clean.length >= 11) {
    return `+229 ${clean.slice(3, 5)} ${clean.slice(5, 7)} ${clean.slice(7, 9)} ${clean.slice(9)}`;
  }
  return clean;
}

function getOperatorShortName(op) {
  const norm = (op || '').toUpperCase();
  if (norm.includes('MTN')) return 'MTN';
  if (norm.includes('MOOV')) return 'MOOV';
  if (norm.includes('CELTIIS')) return 'CEL';
  if (norm.includes('ORANGE')) return 'ORA';
  if (norm.includes('WAVE')) return 'WAV';
  return norm.slice(0, 3) || 'MOM';
}

function getOperatorBadgeClass(op) {
  const norm = (op || '').toUpperCase();
  if (norm.includes('MTN')) return 'bg-amber-100 text-amber-800 border border-amber-200';
  if (norm.includes('MOOV')) return 'bg-blue-100 text-blue-800 border border-blue-200';
  if (norm.includes('CELTIIS')) return 'bg-purple-100 text-purple-800 border border-purple-200';
  return 'bg-slate-100 text-slate-800 border border-slate-200';
}

function calculateQuickAmount(percent) {
  const amount = (balance.value * percent) / 100;
  const calculated = Math.floor(amount / 100) * 100;
  return Math.min(calculated, payoutLimits.value.max);
}

function selectQuickPercentage(percent) {
  form.value.amount = calculateQuickAmount(percent);
  validateAmount();
}

function validateAmount() {
  const amount = Number(form.value.amount);
  if (!amount) {
    errors.value.amount = 'Veuillez entrer un montant';
    return;
  }
  if (amount < payoutLimits.value.min) {
    errors.value.amount = `Le montant minimum est de ${formatAmount(payoutLimits.value.min)} XOF`;
    return;
  }
  if (amount > payoutLimits.value.max) {
    errors.value.amount = `Le montant maximum est de ${formatAmount(payoutLimits.value.max)} XOF`;
    return;
  }
  if (amount > balance.value) {
    errors.value.amount = 'Solde insuffisant';
    return;
  }
  errors.value.amount = null;
}

const isFormValid = computed(() => {
  return (
    form.value.amount &&
    Number(form.value.amount) >= payoutLimits.value.min &&
    Number(form.value.amount) <= payoutLimits.value.max &&
    Number(form.value.amount) <= balance.value &&
    selectedAccount.value &&
    !errors.value.amount
  );
});

async function handleSubmit() {
  if (!isFormValid.value || !selectedAccount.value) return;

  isProcessing.value = true;

  const phone = (selectedAccount.value.accountNumber || '').replace(/\s/g, '');
  const provider = (selectedAccount.value.operator || 'MTN').toLowerCase();

  try {
    // 1. Vérifier quel provider est configuré pour le payout
    const configuredProvider = await getProviderToUse(false);
    const isFedapayPilot = (configuredProvider || '').toLowerCase() === 'fedapay-pilot';

    if (isFedapayPilot) {
      // 2. Si le provider est fedapay-pilot, vérifier si le compte est déjà validé sur FedaPay
      const rawPhone = phone.startsWith('229') && phone.length > 8 ? phone.slice(3) : phone;
      const checkRes = await checkFedapayAccount(rawPhone, selectedAccount.value.operator);

      if (checkRes.exists && checkRes.isVerified) {
        // Le compte est déjà validé sur FedaPay, procéder directement au retrait
        executeWithdrawal(phone, provider);
        return;
      }

      // Si le compte n'est pas encore vérifié sur FedaPay :
      // Informer le client qu'on va ajouter/valider son compte avec un paiement de 1 FCFA
      bus.emit(constants.EVENTS.TOAST_NOTIFICATION.SHOW, {
        type: 'info',
        title: 'Validation requise',
        body: 'Votre compte doit être validé avec un débit de confirmation de 1 FCFA avant de pouvoir effectuer le retrait.'
      });

      if (fedapayVerification.value) {
        fedapayVerification.value.startVerification({
          owner: selectedAccount.value.accountLabel,
          accountLabel: selectedAccount.value.accountLabel,
          phoneNumber: phone,
          rawPhoneNumber: rawPhone,
          operator: selectedAccount.value.operator || 'MTN',
          country: selectedAccount.value.country || 'Bénin'
        });
      }
    } else {
      // Autre provider (ex: FeexPay) : procéder au retrait normalement
      executeWithdrawal(phone, provider);
    }
  } catch (error) {
    console.error('Erreur lors de la vérification préalable au retrait :', error);
    // Fallback: procéder au retrait
    executeWithdrawal(phone, provider);
  }
}

function executeWithdrawal(phone, provider) {
  bus.emit(constants.EVENTS.WALLET.WITHDRAWAL_INITIATE, {
    amount: Number(form.value.amount),
    phoneNumber: phone,
    provider: provider,
    paymentMethod: provider,
    accountLabel: selectedAccount.value.accountLabel,
    currency: 'XOF',
    timestamp: Date.now()
  });
}

function onFedapayVerifiedAndWithdraw() {
  if (!selectedAccount.value) {
    isProcessing.value = false;
    return;
  }
  const phone = (selectedAccount.value.accountNumber || '').replace(/\s/g, '');
  const provider = (selectedAccount.value.operator || 'MTN').toLowerCase();
  executeWithdrawal(phone, provider);
}

function onFedapayCancelled() {
  isProcessing.value = false;
  bus.emit(constants.EVENTS.TOAST_NOTIFICATION.SHOW, {
    type: 'info',
    title: 'Annulé',
    body: "La vérification du compte a été interrompue. Le retrait n'a pas été effectué."
  });
}

function onFedapayError(err) {
  isProcessing.value = false;
  bus.emit(constants.EVENTS.TOAST_NOTIFICATION.SHOW, {
    type: 'error',
    title: 'Erreur de vérification',
    body: err?.message || "Échec de la validation du compte sur Mobile Money."
  });
}

function resetForm() {
  form.value = { amount: '' };
  errors.value = { amount: null };
  isProcessing.value = false;
}

function onBalanceUpdate(data) {
  balance.value = data?.balance || 0;
}

function onAccountsUpdate(data) {
  accounts.value = data?.accounts || [];
  accountsLoading.value = false;
  // If no account currently selected or current selection not in list, auto-select first
  if (accounts.value.length > 0) {
    if (!selectedAccount.value || !accounts.value.find(a => a.id === selectedAccount.value.id)) {
      selectedAccount.value = accounts.value[0];
    }
  } else {
    selectedAccount.value = null;
  }
}

function onAccountCreated(data) {
  if (data?.account) {
    selectedAccount.value = data.account;
  }
}

function onAccountsError() {
  accountsLoading.value = false;
}

function onWithdrawalSuccess() {
  isProcessing.value = false;
  hideModal();
  resetForm();
}

function onWithdrawalError() {
  isProcessing.value = false;
}

onMounted(() => {
  bus.on(constants.EVENTS.WALLET.BALANCE_UPDATE, onBalanceUpdate);
  bus.on(constants.EVENTS.WALLET.ACCOUNTS_UPDATE, onAccountsUpdate);
  bus.on(constants.EVENTS.WALLET.ACCOUNT_CREATED, onAccountCreated);
  bus.on(constants.EVENTS.WALLET.ACCOUNTS_ERROR, onAccountsError);
  bus.on(constants.EVENTS.WALLET.WITHDRAWAL_SUCCESS, onWithdrawalSuccess);
  bus.on(constants.EVENTS.WALLET.WITHDRAWAL_ERROR, onWithdrawalError);
});

onBeforeUnmount(() => {
  bus.off(constants.EVENTS.WALLET.BALANCE_UPDATE, onBalanceUpdate);
  bus.off(constants.EVENTS.WALLET.ACCOUNTS_UPDATE, onAccountsUpdate);
  bus.off(constants.EVENTS.WALLET.ACCOUNT_CREATED, onAccountCreated);
  bus.off(constants.EVENTS.WALLET.ACCOUNTS_ERROR, onAccountsError);
  bus.off(constants.EVENTS.WALLET.WITHDRAWAL_SUCCESS, onWithdrawalSuccess);
  bus.off(constants.EVENTS.WALLET.WITHDRAWAL_ERROR, onWithdrawalError);
});
</script>